import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ValueLabelType } from '@whistler-ui/k2/types';

import {
  createMember,
  getCarrierGroups,
  getGroupMembers,
  getLocalMembers,
  getUngroupedMembers,
  removeMemberFromGroup,
  switchGroupCarrier,
  updateGroup,
  updateMember,
} from '../../services';

export const useGetLocalMembers = () => {
  return useQuery({
    queryKey: ['get_members'],
    queryFn: () => getLocalMembers(),
    retry: 1,
    select: data => {
      if (Array.isArray(data)) {
        return data.sort(({ email: a }, { email: b }) => {
          return a < b ? -1 : a > b ? 1 : 0;
        });
      }
      return data;
    },
  });
};

export const useUpdateMember = () => {
  return useMutation({
    mutationKey: ['update_member'],
    mutationFn: updateMember,
  });
};

export const useCreateMember = () => {
  return useMutation({
    mutationKey: ['create_member'],
    mutationFn: createMember,
  });
};

export const useGetGroupMembers = () => {
  return useQuery({
    queryKey: ['get_group_members'],
    queryFn: () => getGroupMembers(),
    retry: 1,
  });
};

export const useGetUngroupedMembers = () => {
  return useQuery({
    queryKey: ['get_ungrouped_members'],
    queryFn: () => getUngroupedMembers(),
    retry: 1,
  });
};

export const useUpdateGroup = (params?: { invalidateQueries?: boolean }) => {
  const { invalidateQueries = true } = params || {};

  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['update_group'],
    mutationFn: updateGroup,
    onSuccess: () => {
      if (!invalidateQueries) return;

      queryClient.invalidateQueries({
        queryKey: ['get_group_members'],
      });
      queryClient.invalidateQueries({
        queryKey: ['get_ungrouped_members'],
      });
    },
  });
};

export const useRemoveMemberFromGroup = (params?: {
  invalidateQueries?: boolean;
}) => {
  const { invalidateQueries = true } = params || {};

  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['remove_member_from_group'],
    mutationFn: removeMemberFromGroup,
    onSuccess: () => {
      if (!invalidateQueries) return;

      queryClient.invalidateQueries({
        queryKey: ['get_group_members'],
      });
      queryClient.invalidateQueries({
        queryKey: ['get_ungrouped_members'],
      });
    },
  });
};

export const useRefreshMembers = () => {
  const queryClient = useQueryClient();

  return async () => {
    await queryClient.invalidateQueries({
      queryKey: ['get_members'],
    });

    await queryClient.invalidateQueries({
      queryKey: ['get_group_members'],
    });

    await queryClient.invalidateQueries({
      queryKey: ['get_ungrouped_members'],
    });
  };
};

export const useGetCarrierGroups = (params?: { isGroupCarrier: boolean }) => {
  const { isGroupCarrier = false } = params || {};

  return useQuery({
    queryKey: ['get_carrier_groups'],
    queryFn: () => getCarrierGroups(),
    retry: 1,
    select: data => {
      if (data && Array.isArray(data) && data.length > 0) {
        return data.map(group => {
          return {
            value: group.id,
            label: group.name,
          } as ValueLabelType;
        });
      }
      return [];
    },
    enabled: isGroupCarrier,
  });
};

export const useSwitchGroupCarrier = () => {
  return useMutation({
    mutationKey: ['switch_group_carrier'],
    mutationFn: switchGroupCarrier,
  });
};
